import React from 'react';

import {Icon, WithTooltip} from '@albert-io/atomic';

import {STUDENT_RESTRICTION_MESSAGE} from './studentContentLock';

interface Props {
  className?: string;
  placement?: React.ComponentProps<typeof WithTooltip>['placement'];
}

/**
 * Per-question lock indicator for student surfaces (admin-locked-assessments, Part 4). A binary
 * full shield with a generic tooltip — students never see a partial/half shield or the restricting
 * school (`references/ui-patterns.md`). Render it only for a restricted question (e.g. gated by
 * `isQuestionRestrictedForStudent`); it carries no decision of its own.
 *
 * Implemented as a non-interactive icon with a hover tooltip rather than a click-popover so it
 * can sit inside the navigators' `<button>` rows without nesting interactive elements (which would
 * be an accessibility violation). The row itself stays clickable and surfaces the same block-and-
 * message when opened, per the "clicking surfaces the same message" rule in `references/ui-patterns.md`,
 * so keyboard/screen-reader users reach the explanation via the row even though the tooltip is hover-only.
 */
export function StudentRestrictionIndicator({className, placement = 'top'}: Props) {
  return (
    <WithTooltip content={STUDENT_RESTRICTION_MESSAGE} placement={placement}>
      <Icon
        icon='shield'
        iconStyle='solid'
        color='negative'
        aria-label='Restricted question'
        className={className}
      />
    </WithTooltip>
  );
}
